import { Fragment } from "preact"
import { useState } from "preact/hooks"
import { ChevronDown, ChevronRight, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { AutocompleteSelect, SelectItem } from "@/accounting/autocomplete_select.tsx"
import type { CustomerGroupPreview, InvoiceAssignmentLine, QbCustomer } from "@/api/accounting"
import {
  ClearingTransfer,
  CustomerSide,
  customer_group_label,
  merge_customer_sides,
  payment_cents_by_customer,
  plan_clearing_transfers,
  to_cents,
} from "@/accounting/customer_groups"
import { exactCurrencyFormatter } from "@/utils"

interface CustomerGroupsCardProps {
  accounting_system: string
  groups: CustomerGroupPreview[]
  /** Every row on the check; each carries the `customer_group_key` of the group it belongs to. */
  lines: InvoiceAssignmentLine[]
  customers: QbCustomer[]
  /** Chosen customer remote id per group key. */
  selections: Record<string, string>
  on_select: (key: string, customer_id: string) => void
  className?: string
}

function cents(amount_cents: number): string {
  return exactCurrencyFormatter(amount_cents / 100)
}

/**
 * Review and assign the customers of a check that spans several Ahold vendor accounts: each
 * group's remittance subtotals and rows, a customer per group, and what will post as a result —
 * one payment per customer, plus the dummy invoice and dummy credit memo when a side nets negative.
 */
export function CustomerGroupsCard({ accounting_system, groups, lines, customers, selections, on_select, className }: CustomerGroupsCardProps) {
  const [expanded, set_expanded] = useState<Set<string>>(() => new Set())

  function toggle(key: string) {
    set_expanded((prev: Set<string>) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const sides = merge_customer_sides(groups, selections, customers)
  const { transfers, shortfall_cents } = plan_clearing_transfers(sides)
  const payments = payment_cents_by_customer(sides, transfers)
  const name_of = (customer_id: string) => sides.find((s: CustomerSide) => s.customer_id === customer_id)?.customer_name ?? customer_id
  const customer_items = customers.map((c: QbCustomer) => ({ id: c.remote_id, name: c.display_name }))

  return (
    <Card class={`shadow-sm border-slate-200/80 ${className ?? ""}`}>
      <CardHeader class="space-y-1 pb-4">
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <Users className="h-4 w-4 text-slate-500" />
          Customers on this payment
        </CardTitle>
        <CardDescription>
          This Ahold payment covers {groups.filter((g: CustomerGroupPreview) => g.key !== "").length} vendor accounts (the remittance's
          "Legacy Vendor/Customer" column). Pick the{" "}
          {accounting_system} customer each one posts to — applying creates one credit memo and one payment per customer, and remembers
          your choices for the next payment.
        </CardDescription>
      </CardHeader>
      <CardContent class="space-y-4">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-200 text-left text-xs text-slate-500">
                <th class="py-2 pr-3 font-medium">Vendor account</th>
                <th class="py-2 pr-3 font-medium text-right">Invoices paid</th>
                <th class="py-2 pr-3 font-medium text-right">Deductions</th>
                <th class="py-2 pr-3 font-medium text-right">Net</th>
                <th class="py-2 font-medium w-72">{accounting_system} customer</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group: CustomerGroupPreview) => {
                const is_open = expanded.has(group.key)
                const group_lines = lines.filter((l: InvoiceAssignmentLine) => (l.customer_group_key ?? "") === group.key)
                const selected = selections[group.key]
                return (
                  <Fragment key={group.key}>
                    <tr class="border-b border-slate-100">
                      <td class="py-2 pr-3">
                        <Button type="button" variant="ghost" size="sm" className="h-auto gap-1.5 px-1 py-0.5 font-normal" onClick={() => toggle(group.key)}>
                          {is_open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          <span class="font-medium">{customer_group_label(group.key)}</span>
                          <span class="text-xs text-slate-500">
                            {group.transaction_count} row{group.transaction_count === 1 ? "" : "s"}
                          </span>
                        </Button>
                      </td>
                      <td class="py-2 pr-3 text-right font-mono">{exactCurrencyFormatter(Number(group.invoice_cash))}</td>
                      <td class="py-2 pr-3 text-right font-mono">{exactCurrencyFormatter(Number(group.deduction_total))}</td>
                      <td class={`py-2 pr-3 text-right font-mono font-medium ${to_cents(group.net) < 0 ? "text-rose-700" : ""}`}>
                        {exactCurrencyFormatter(Number(group.net))}
                      </td>
                      <td class="py-2">
                        <AutocompleteSelect
                          searchPlaceholder="Search customers..."
                          emptyText={`No ${accounting_system} customers synced yet.`}
                          values={customer_items}
                          initial={selected ? { id: selected, name: customers.find((c: QbCustomer) => c.remote_id === selected)?.display_name ?? selected } : undefined}
                          onSelect={(item: SelectItem) => on_select(group.key, item.id)}
                        />
                      </td>
                    </tr>
                    {is_open ? (
                      <tr class="border-b border-slate-100 bg-slate-50/60">
                        <td colSpan={5} class="px-6 py-2">
                          <table class="w-full text-xs">
                            <tbody>
                              {group_lines.map((line: InvoiceAssignmentLine) => (
                                <tr key={line.deduction_id}>
                                  <td class="py-0.5 pr-3 font-mono">{line.invoice_number}</td>
                                  <td class="py-0.5 pr-3 capitalize text-slate-500">{line.kind}</td>
                                  <td class="py-0.5 text-right font-mono">{exactCurrencyFormatter(line.deduction_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>

        {sides.length > 0 ? (
          <div class="rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900 space-y-1.5">
            <p class="font-medium">{sides.length === 1 ? "What will post: one payment" : `What will post: ${sides.length} customers`}</p>
            <ul class="space-y-0.5">
              {sides.map((side: CustomerSide) => (
                <li key={side.customer_id} class="flex justify-between gap-4">
                  <span>
                    {side.customer_name} <span class="text-sky-700">({side.keys.map(customer_group_label).join(", ")})</span>
                  </span>
                  <span class="font-mono">
                    {payments[side.customer_id] <= 0 ? "credit memo applied, no payment" : `payment ${cents(payments[side.customer_id])}`}
                  </span>
                </li>
              ))}
            </ul>
            {transfers.map((t: ClearingTransfer) => (
              <p key={`${t.deficit_customer_id}-${t.donor_customer_id}`}>
                {name_of(t.deficit_customer_id)}'s deductions exceed its invoices by {cents(t.amount_cents)}, so that much moves through your AR
                clearing account: a dummy invoice on {name_of(t.deficit_customer_id)} (settled by its credit memo with its invoices) and a dummy
                credit memo on {name_of(t.donor_customer_id)} (applied {payments[t.donor_customer_id] > 0 ? "on its payment" : "to its invoices"}).
                The clearing account nets to zero.
              </p>
            ))}
            {shortfall_cents > 0 ? (
              <p>
                This payment's deductions exceed everything else on it by {cents(shortfall_cents)}. That much stays as open credit on the credit memo
                — in open deductions, not the clearing account — until it is investigated or the customer takes it on a later payment.
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
